import { CheckCircle, Clock, DollarSign, Percent, Users } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import ReactTable from 'react-table';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Row,
} from 'reactstrap';
import { Colxx } from '../../../components/common/CustomBootstrap';
import DataTablePagination from '../../../components/DatatablePagination';
import StatCard from '../../../components/dashboard/StatCard';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';

const STATUS_COLORS = {
  pending: 'warning',
  converted: 'info',
  rewarded: 'success',
};

const ReferralManagement = () => {
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [settings, setSettings] = useState({
    active: true,
    referrerReward: 0,
    referredReward: 0,
    expiration: 30,
  });
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [referralDoc, referralsSnap] = await Promise.all([
          firestore().collection('settings').doc('referral').get(),
          firestore()
            .collection('settings')
            .doc('referral')
            .collection('referrals')
            .orderBy('createdAt', 'desc')
            .limit(200)
            .get(),
        ]);

        if (referralDoc.exists) {
          const data = referralDoc.data();
          setSettings({
            active: data.active !== false,
            referrerReward: Number(data.referrerReward) || 0,
            referredReward: Number(data.referredReward) || 0,
            expiration: Number(data.expiration) || 30,
          });
        }

        setReferrals(
          referralsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      } catch (error) {
        console.error('Error loading referral data:', error);
        createNotification('error', 'Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const stats = useMemo(() => {
    const total = referrals.length;
    const pending = referrals.filter((r) => r.status === 'pending').length;
    const rewarded = referrals.filter((r) => r.status === 'rewarded').length;
    const conversionRate = total > 0 ? ((rewarded / total) * 100).toFixed(1) : 0;
    const totalReferrerPaid = referrals
      .filter((r) => r.status === 'rewarded')
      .reduce((sum, r) => sum + (Number(r.referrerReward) || 0), 0);
    const totalReferredPaid = referrals.reduce(
      (sum, r) => sum + (Number(r.referredReward) || 0),
      0
    );
    return {
      total,
      pending,
      rewarded,
      conversionRate,
      totalReferrerPaid,
      totalReferredPaid,
      totalPaid: totalReferrerPaid + totalReferredPaid,
    };
  }, [referrals]);

  const filteredReferrals = useMemo(() => {
    if (statusFilter === 'all') return referrals;
    return referrals.filter((r) => r.status === statusFilter);
  }, [referrals, statusFilter]);

  const tableColumns = useMemo(() => [
    {
      Header: 'Referrer',
      accessor: 'referrerName',
      Cell: ({ original }) => (
        <div>
          <div style={{ fontWeight: 600 }}>{original.referrerName || '---'}</div>
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
            {original.referrerNumber || original.referrerUID}
          </small>
        </div>
      ),
    },
    {
      Header: 'Referred User',
      accessor: 'referredName',
      Cell: ({ original }) => (
        <div>
          <div style={{ fontWeight: 600 }}>{original.referredName || '---'}</div>
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
            {original.referredNumber || original.referredUID}
          </small>
        </div>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: 120,
      Cell: ({ value }) => (
        <Badge
          color={STATUS_COLORS[value] || 'secondary'}
          style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem' }}
        >
          {value || 'unknown'}
        </Badge>
      ),
    },
    {
      Header: 'Rewards',
      accessor: 'referredReward',
      Cell: ({ original }) => (
        <div>
          <div style={{ fontSize: '0.8rem' }}>
            <span className="text-muted">Referred: </span>
            <strong>AED {Number(original.referredReward || 0).toFixed(2)}</strong>
          </div>
          {original.status === 'rewarded' && (
            <div style={{ fontSize: '0.8rem' }}>
              <span className="text-muted">Referrer: </span>
              <strong>AED {Number(original.referrerReward || 0).toFixed(2)}</strong>
            </div>
          )}
        </div>
      ),
    },
    {
      Header: 'Date',
      accessor: 'createdAt',
      width: 130,
      Cell: ({ value }) => value ? moment(value).format('MMM DD, YYYY') : '---',
    },
    {
      Header: 'Converted',
      accessor: 'convertedAt',
      width: 130,
      Cell: ({ value }) => value ? moment(value).format('MMM DD, YYYY') : <span className="text-muted">---</span>,
    },
  ], []);


  if (loading) {
    return <div className="loading" />;
  }

  return (
    <div>
      {/* Header */}
      <Row>
        <Colxx xxs="5" className="mb-3">
          <h1 className="mb-2 p-0">Referral Program</h1>
        </Colxx>
        <Colxx xxs="7" className="mb-3">
          <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
            <div
              className="px-3 py-2 mr-2"
              style={{
                background: settings.active ? '#dcfce7' : '#fce4ec',
                borderRadius: 8,
              }}
            >
              <strong style={{ color: settings.active ? '#16a34a' : '#c62828', fontSize: '0.8rem' }}>
                {settings.active ? 'ACTIVE' : 'DISABLED'}
              </strong>
            </div>
          </div>
        </Colxx>
      </Row>

      {/* Stat Cards */}
      <Row className="mt-2">
        <Colxx xxs="12" sm="6" lg className="mb-4">
          <StatCard
            title="Total Referrals"
            value={stats.total}
            icon={Users}
            color="#3b82f6"
          />
        </Colxx>
        <Colxx xxs="12" sm="6" lg className="mb-4">
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={Clock}
            color="#f59e0b"
          />
        </Colxx>
        <Colxx xxs="12" sm="6" lg className="mb-4">
          <StatCard
            title="Converted"
            value={stats.rewarded}
            icon={CheckCircle}
            color="#10b981"
          />
        </Colxx>
        <Colxx xxs="12" sm="6" lg className="mb-4">
          <StatCard
            title="Conversion Rate"
            value={`${stats.conversionRate}%`}
            icon={Percent}
            color="#8b5cf6"
          />
        </Colxx>
        <Colxx xxs="12" sm="6" lg className="mb-4">
          <StatCard
            title="Total Rewards Paid"
            value={stats.totalPaid}
            icon={DollarSign}
            color="#ef4444"
            formatCurrency={true}
          />
        </Colxx>
      </Row>

      {/* Referral History */}
      <Row>
        <Colxx xxs="12" className="mb-4">
          <Card style={{ borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Referral History</h5>
                <div className="d-flex" style={{ gap: 6 }}>
                  {['all', 'pending', 'rewarded'].map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      color={statusFilter === status ? 'primary' : 'light'}
                      onClick={() => setStatusFilter(status)}
                      style={{ borderRadius: 6, textTransform: 'capitalize', fontSize: '0.8rem' }}
                    >
                      {status === 'all' ? `All (${referrals.length})` :
                        status === 'pending' ? `Pending (${stats.pending})` :
                          `Converted (${stats.rewarded})`}
                    </Button>
                  ))}
                </div>
              </div>

              {filteredReferrals.length === 0 ? (
                <div className="text-center py-5">
                  <Users size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
                  <p className="text-muted mb-0">
                    {referrals.length === 0
                      ? 'No referrals tracked yet. Data will appear once users start sharing referral links.'
                      : 'No referrals match the selected filter.'}
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', width: '100%' }} className="react-table-scroll-wrapper">
                  <ReactTable
                    data={filteredReferrals}
                    columns={tableColumns}
                    defaultPageSize={10}
                    minRows={0}
                    showPageJump={false}
                    showPageSizeOptions={false}
                    PaginationComponent={DataTablePagination}
                    className="custuomReactTable"
                    defaultSorted={[{ id: 'createdAt', desc: true }]}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Colxx>
      </Row>

    </div>
  );
};

export default ReferralManagement;
